import React from 'react';
import { 
  Box, 
  Typography, 
  Container,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme
} from '@mui/material';
import {
  Code as CodeIcon,
  School as SchoolIcon,
  WorkOutline as WorkIcon,
  Favorite as FavoriteIcon
} from '@mui/icons-material';

const About: React.FC = () => {
  const theme = useTheme();
  
  return (
    <Box sx={{ py: 10 }}>
      <Container maxWidth="lg">
        <Typography 
          variant="h2" 
          textAlign="center"
          gutterBottom
          sx={{ mb: 2 }}
        >
          About Me
        </Typography>
        <Typography 
          variant="h6" 
          component="p" 
          textAlign="center" 
          color="text.secondary"
          sx={{ 
            maxWidth: 'md',
            mx: 'auto',
            mb: 6
          }}
        >
          My background, experience, and passion for software development
        </Typography>
        
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '5fr 7fr' }, gap: 6 }}>
          <Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                mb: { xs: 4, md: 0 }
              }}
            >
              <Box
                sx={{
                  width: 280,
                  height: 280,
                  position: 'relative',
                  mb: 3
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    position: 'relative',
                    zIndex: 1,
                    overflow: 'hidden',
                    borderRadius: '10px',
                    border: `2px solid ${theme.palette.primary.main}`,
                    img: {
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      transition: '0.3s ease-in-out',
                      filter: 'grayscale(100%)'
                    },
                    '&:hover img': {
                      filter: 'grayscale(0%)'
                    }
                  }}
                >
                  <img 
                    src="/KD1.jpg" 
                    alt="KHD" 
                  />
                </Box>
                <Box
                  sx={{
                    position: 'absolute',
                    top: 20,
                    left: 20,
                    width: '100%',
                    height: '100%',
                    border: `2px solid ${theme.palette.primary.main}`,
                    borderRadius: '10px',
                    zIndex: 0
                  }}
                />
              </Box>
              
              <Typography variant="h5" textAlign="center" gutterBottom>
                Me and My Dog
              </Typography>
              <Typography variant="subtitle1" color="primary" textAlign="center">
                Principal Engineer @ AVEVA
              </Typography>
            </Box>
          </Box>
          
          <Box>
            <Paper 
              elevation={2} 
              sx={{ 
                p: 4,
                height: '100%',
                borderRadius: 2,
                backgroundColor: 'background.paper'
              }}
            >
              <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
                My Story
              </Typography>
              
              <Typography paragraph>
                I'm Keith, a Principal Engineer based in Northern Ireland with over 12 years of experience in software development. 
                I started my journey at 8over8 in 2013 as a Junior Developer, where I discovered my passion for building 
                robust web applications and learning new technologies.
              </Typography>
              
              <Typography paragraph>
                Throughout my career, I've been fortunate to work across the entire stack, from designing intuitive user 
                interfaces to architecting scalable backend systems. My experience spans various domains including enterprise 
                software, data visualization, and application development.
              </Typography>
              
              <Typography paragraph>
                As a principal engineer at AVEVA, I now focus on designing and implementing large-scale solutions, mentoring 
                other developers, and ensuring we follow best practices in our development process. I'm particularly passionate 
                about creating clean, maintainable code and building intuitive user experiences.
              </Typography>
              
              <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
                What I Do
              </Typography>
              
              <List>
                <ListItem disableGutters>
                  <ListItemIcon sx={{ color: 'primary.main', minWidth: 40 }}>
                    <CodeIcon />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Full Stack Development" 
                    secondary="Building end-to-end solutions with modern technologies" 
                  />
                </ListItem>
                
                <ListItem disableGutters>
                  <ListItemIcon sx={{ color: 'primary.main', minWidth: 40 }}>
                    <SchoolIcon />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Mentorship & Training" 
                    secondary="Guiding junior developers and sharing knowledge across teams" 
                  />
                </ListItem>
                
                <ListItem disableGutters>
                  <ListItemIcon sx={{ color: 'primary.main', minWidth: 40 }}>
                    <WorkIcon />
                  </ListItemIcon>
                  <ListItemText 
                    primary="System Architecture" 
                    secondary="Designing robust, scalable, and maintainable software systems" 
                  />
                </ListItem>
                
                <ListItem disableGutters>
                  <ListItemIcon sx={{ color: 'primary.main', minWidth: 40 }}>
                    <FavoriteIcon />
                  </ListItemIcon>
                  <ListItemText 
                    primary="User Experience" 
                    secondary="Creating intuitive and accessible interfaces" 
                  />
                </ListItem>
              </List>
            </Paper>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default About;
